import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tryRefresh, LONG_REFRESH_KEY } from "./api";
import { APP_VERSION } from "./version";

const AuthCtx = createContext(null);

// v144 — علم على مستوى الوحدة يمنع تبادل session_id مرتين (StrictMode double-mount)
let _googleExchangeStarted = false;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);          // null = checking
  const [authed, setAuthed] = useState(undefined); // undefined = checking, true/false otherwise
  const [permissions, setPermissions] = useState(null); // null = not loaded; Set<string> once loaded

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/permissions");
      const set = new Set(data?.permissions || []);
      setPermissions(set);
      sessionStorage.setItem("tw_perms", JSON.stringify([...set]));
    } catch {
      setPermissions(new Set());
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      // Short timeout (8s) so a stalled backend doesn't pin the app in the
      // "verifying session..." splash forever. Long-running AI endpoints
      // (e.g., CEO meeting generation) used to block subsequent /auth/me
      // calls and freeze the whole UI (v118.8 fix).
      let data;
      try {
        ({ data } = await api.get("/auth/me", { timeout: 8000 }));
      } catch (e0) {
        if (e0?.response?.status !== 401 || !localStorage.getItem(LONG_REFRESH_KEY) || !(await tryRefresh())) throw e0;
        ({ data } = await api.get("/auth/me", { timeout: 8000 }));
      }
      setUser(data);
      sessionStorage.setItem("tw_user", JSON.stringify(data));
      setAuthed(true);
      // v119.220 — سجّل آخر نسخة يفتحها هذا الجهاز (لمراقبة تحديث الفريق)
      api.post("/auth/client-version", { version: APP_VERSION }).catch(() => {});
      // Permissions are role-bound but stored separately so the editor can
      // change a role's perms without forcing a full re-login.
      await loadPermissions();
    } catch (err) {
      // If the failure is a timeout (backend busy) — keep the cached session
      // alive so the user doesn't get bounced to login. Only clear on a real
      // 401/403.
      const status = err?.response?.status;
      const isTimeout = err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
      if (isTimeout && sessionStorage.getItem("tw_user")) {
        try {
          setUser(JSON.parse(sessionStorage.getItem("tw_user")));
          setAuthed(true);
          // Best-effort: refresh perms in the background without blocking.
          loadPermissions().catch(() => {});
          return;
        } catch (_e) { /* fall through to logout */ }
      }
      if (status === 401 || status === 403 || !isTimeout) {
        sessionStorage.removeItem("tw_user");
        sessionStorage.removeItem("tw_perms");
        localStorage.removeItem("tw_token");
        localStorage.removeItem("tw_user");
        setUser(null);
        setAuthed(false);
        setPermissions(new Set());
      } else {
        // Final fallback for non-401/403 timeouts with no cache: send to login.
        setAuthed(false);
        setUser(null);
        setPermissions(new Set());
      }
    }
  }, [loadPermissions]);

  const login = useCallback(async (username, password, otp, remember = false) => {
    const body = { username, password, remember: !!remember };
    if (otp) body.otp = otp.trim();
    const { data } = await api.post("/auth/login", body);
    if (data.token) sessionStorage.setItem("tw_token", data.token);
    if (data.refresh && remember) localStorage.setItem(LONG_REFRESH_KEY, data.refresh);
    else if (data.refresh) sessionStorage.setItem("tw_refresh", data.refresh);
    if (!remember) localStorage.removeItem(LONG_REFRESH_KEY);
    sessionStorage.setItem("tw_user", JSON.stringify(data.user));
    localStorage.removeItem("tw_token");
    localStorage.removeItem("tw_user");
    setUser(data.user);
    setAuthed(true);
    await loadPermissions();
    return data.user;
  }, [loadPermissions]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* server unreachable — clear locally anyway */ }
    localStorage.removeItem(LONG_REFRESH_KEY);
    sessionStorage.removeItem("tw_token");
    sessionStorage.removeItem("tw_user");
    sessionStorage.removeItem("tw_perms");
    localStorage.removeItem("tw_token");
    localStorage.removeItem("tw_user");
    setUser(null);
    setAuthed(false);
    setPermissions(new Set());
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem("tw_user");
      localStorage.removeItem("tw_token");
      const cached = sessionStorage.getItem("tw_user");
      if (cached) setUser(JSON.parse(cached));
      const cachedPerms = sessionStorage.getItem("tw_perms");
      if (cachedPerms) setPermissions(new Set(JSON.parse(cachedPerms)));
    } catch (e) {
      console.warn("[auth] failed to parse cached user", e);
    }
    // v144 — العودة من الدخول بحساب Google (Emergent Auth):
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // نتحقق من session_id في الـ hash قبل استدعاء /auth/me لتفادي سباق الجلسات.
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      if (!_googleExchangeStarted) {
        _googleExchangeStarted = true;
        (async () => {
        try {
          const { data } = await api.post("/auth/google/session", {}, {
            headers: { "X-Session-ID": sid },
          });
          // ننظف الـ hash فقط بعد اكتمال التبادل — إعادة تحميل SW قد تجهض الطلب،
          // وبقاء الـ hash يسمح بإعادة المحاولة تلقائياً بعد التحميل.
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          if (data.token) sessionStorage.setItem("tw_token", data.token);
          if (data.refresh) sessionStorage.setItem("tw_refresh", data.refresh);
          sessionStorage.setItem("tw_user", JSON.stringify(data.user));
          setUser(data.user);
          setAuthed(true);
          await loadPermissions();
        } catch (err) {
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          const d = err?.response?.data?.detail;
          const msg = typeof d === "string" ? d : "فشل الدخول بحساب Google — أعد المحاولة";
          sessionStorage.setItem("tw_google_err", msg);
          setUser(null);
          setAuthed(false);
          window.dispatchEvent(new CustomEvent("google-auth-error", { detail: msg }));
        }
        })();
      }
      const onLogoutG = () => logout();
      window.addEventListener("auth:logout", onLogoutG);
      return () => window.removeEventListener("auth:logout", onLogoutG);
    }
    refresh();
    const onLogout = () => logout();
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [refresh, logout, loadPermissions]);

  // hasPerm — central authority for all UI guards. Admin always passes
  // (defence-in-depth: even if the backend hasn't pushed updated perms yet).
  const hasPerm = useCallback((perm) => {
    if (!user) return false;
    if ((user.role || "").toLowerCase() === "admin") return true;
    if (!perm) return true;
    return permissions ? permissions.has(perm) : false;
  }, [user, permissions]);

  return (
    <AuthCtx.Provider value={{ user, authed, login, logout, refresh, permissions, hasPerm, loadPermissions }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
